/**
 * Group Booking API Contract Schemas
 *
 * Enums mirror the Prisma model (canonical source of truth):
 *   GroupType, GroupBookingStatus, GroupMemberStatus, GroupPaymentMode
 */

import { z } from 'zod';

// ============================================================================
// Enums (canonical: Prisma GroupBooking model)
// ============================================================================

export const GroupTypeSchema = z.enum(['FAMILY', 'SCHOOL', 'CORPORATE', 'TOUR', 'EVENT', 'OTHER']);
export type GroupTypeValue = z.infer<typeof GroupTypeSchema>;

export const GroupBookingStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'REJECTED',
  'CONFIRMED',
  'DEPOSIT_PAID',
  'PAID',
  'CHECKED_IN',
  'COMPLETED',
  'CANCELLED',
  'REFUND_PENDING',
]);
export type GroupBookingStatusValue = z.infer<typeof GroupBookingStatusSchema>;

export const GroupMemberStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'CHECKED_IN',
  'NO_SHOW',
  'CANCELLED',
]);
export type GroupMemberStatusValue = z.infer<typeof GroupMemberStatusSchema>;

export const GroupPaymentModeSchema = z.enum(['FULL_UPFRONT', 'DEPOSIT_BALANCE']);
export type GroupPaymentModeValue = z.infer<typeof GroupPaymentModeSchema>;

export const WristbandDistributionStatusSchema = z.enum(['HELD', 'DISTRIBUTED', 'LATE_PICKUP']);
export type WristbandDistributionStatus = z.infer<typeof WristbandDistributionStatusSchema>;

// ============================================================================
// Shared Nested Input Schemas
// ============================================================================

export const TicketLineInputSchema = z.object({
  ticketTypeId: z.string().uuid(),
  quantity: z.number().int().min(1),
});
export type TicketLineInput = z.infer<typeof TicketLineInputSchema>;

export const AddonLineInputSchema = z.object({
  attractionTicketTypeId: z.string().uuid(),
  quantity: z.number().int().min(1),
  bundleCode: z.string().max(50).optional(),
  slotId: z.string().optional(),
});
export type AddonLineInput = z.infer<typeof AddonLineInputSchema>;

// ============================================================================
// Request Schemas
// ============================================================================

export const CreateGroupBookingSchema = z.object({
  groupType: GroupTypeSchema,
  groupName: z.string().min(1).max(200),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'visitDate must be YYYY-MM-DD' }),
  arrivalTime: z.string().max(5).optional(),
  expectedMembers: z.number().int().min(2).max(10000),
  ticketLines: z.array(TicketLineInputSchema).min(1).optional(),
  ticketTypeId: z.string().uuid().optional(),
  addonLines: z.array(AddonLineInputSchema).optional(),
  specialRequirements: z.string().max(2000).optional(),
  coordinatorName: z.string().min(1).max(100),
  // ⚠️ Capped at the width of the column that stores it (#870).
  // `group_bookings.coordinator_email` and `group_orders.coordinator_email` are
  // both `@db.VarChar(200)`. Uncapped, a longer address reached Postgres and
  // came back as SQLSTATE 22001 — a 500 for what is a client error. This was the
  // lone uncapped string among capped siblings on this schema.
  //
  // Until #870 the overflow bit much earlier and in a stranger place: the
  // controller prefixed `guest:` and wrote the result into a `VarChar(50)`
  // coordinator id, so a 45-character address failed group creation outright.
  // Widening that column to TEXT removes the accidental ceiling, which is what
  // makes this cap load-bearing rather than tidy.
  //
  // ⚠️ **Updated 2026-08-13 by #855: this cap no longer bounds `coordinator_id`.**
  // It used to, transitively — reseller-service was the last path that minted
  // `guest:<email>`, producing at most 6 + 200 = 206 characters, and that ceiling
  // existed solely because of the number on this line. That mint site now refuses
  // with 422 instead, so every `coordinator_id` written from here on is a 25-char
  // cuid and this cap constrains only the email column, as its name suggests.
  //
  // The cap stays load-bearing for `coordinator_email` itself (the SQLSTATE 22001
  // story above). What is gone is the *second*, unstated job it was doing.
  // Historic rows up to 206 characters still exist, so anything reading
  // `coordinator_id` must still tolerate them — see #931.
  // Read this before touching it, and update
  // `event-schemas/src/__tests__/group-coordinator-id.spec.ts`, which derives
  // its worst case from this number.
  coordinatorEmail: z.string().email().max(200),
  coordinatorPhone: z.string().max(30).optional(),
  schoolName: z.string().max(200).optional(),
  emergencyContactName: z.string().max(100).optional(),
  emergencyContactPhone: z.string().max(30).optional(),
  companyName: z.string().max(200).optional(),
  purchaseOrderNumber: z.string().max(50).optional(),
  notes: z.string().max(2000).optional(),
  idempotencyKey: z.string().max(100).optional(),
  sourceChannel: z.string().max(50).optional(),
  resellerId: z.string().max(50).optional(),
});
export type CreateGroupBookingRequest = z.infer<typeof CreateGroupBookingSchema>;

export const UpdateGroupBookingSchema = z.object({
  groupName: z.string().max(200).optional(),
  visitDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  arrivalTime: z.string().max(5).optional(),
  expectedMembers: z.number().int().min(2).max(10000).optional(),
  specialRequirements: z.string().max(2000).optional(),
  schoolName: z.string().max(200).optional(),
  emergencyContactName: z.string().max(100).optional(),
  emergencyContactPhone: z.string().max(30).optional(),
  companyName: z.string().max(200).optional(),
  purchaseOrderNumber: z.string().max(50).optional(),
  notes: z.string().max(2000).optional(),
});
export type UpdateGroupBookingRequest = z.infer<typeof UpdateGroupBookingSchema>;

export const SubmitGroupBookingSchema = z.object({
  paymentMethod: z.string().min(1).max(50).optional(),
  acceptTerms: z.boolean(),
  idempotencyKey: z.string().max(100).optional(),
  inquiryOnly: z.boolean().optional(),
});
export type SubmitGroupBookingRequest = z.infer<typeof SubmitGroupBookingSchema>;

export const CancelGroupBookingSchema = z.object({
  reason: z.string().max(2000).optional(),
});
export type CancelGroupBookingRequest = z.infer<typeof CancelGroupBookingSchema>;

export const PayBalanceSchema = z.object({
  paymentMethod: z.string().min(1).max(50),
  idempotencyKey: z.string().max(100).optional(),
});
export type PayBalanceRequest = z.infer<typeof PayBalanceSchema>;

export const AddGroupMemberSchema = z.object({
  memberName: z.string().min(1).max(100),
  memberEmail: z.string().email().optional(),
  memberPhone: z.string().max(30).optional(),
  spendingLimitIdr: z.number().int().min(0).optional(),
  notes: z.string().max(500).optional(),
});
export type AddGroupMemberRequest = z.infer<typeof AddGroupMemberSchema>;

export const UpdateGroupMemberSchema = z.object({
  memberName: z.string().max(100).optional(),
  memberEmail: z.string().email().optional(),
  memberPhone: z.string().max(30).optional(),
  spendingLimitIdr: z.number().int().min(0).optional(),
  notes: z.string().max(500).optional(),
});
export type UpdateGroupMemberRequest = z.infer<typeof UpdateGroupMemberSchema>;

export const BulkAddGroupMembersSchema = z.object({
  members: z.array(AddGroupMemberSchema).min(1).max(500),
});
export type BulkAddGroupMembersRequest = z.infer<typeof BulkAddGroupMembersSchema>;

export const CalculateGroupPriceSchema = z.object({
  groupType: GroupTypeSchema,
  expectedMembers: z.number().int().min(2).max(10000),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'visitDate must be YYYY-MM-DD' }),
  ticketTypeId: z.string().uuid().optional(),
  ticketLines: z.array(TicketLineInputSchema).min(1).optional(),
  addonLines: z.array(AddonLineInputSchema).optional(),
  couponCode: z.string().max(50).optional(),
});
export type CalculateGroupPriceRequest = z.infer<typeof CalculateGroupPriceSchema>;

// ============================================================================
// Query Schemas
// ============================================================================

const GROUP_VALID_STATUSES = GroupBookingStatusSchema.options;
const STATUS_PATTERN = new RegExp(
  `^(${GROUP_VALID_STATUSES.join('|')})(,(${GROUP_VALID_STATUSES.join('|')}))*$`
);

export const GroupBookingQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: z
    .string()
    .regex(STATUS_PATTERN, {
      message: `status must be one or more of: ${GROUP_VALID_STATUSES.join(', ')}`,
    })
    .optional(),
  search: z.string().optional(),
});
export type GroupBookingQuery = z.input<typeof GroupBookingQuerySchema>;

export const GroupMemberQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  status: GroupMemberStatusSchema.optional(),
  search: z.string().optional(),
});
export type GroupMemberQuery = z.input<typeof GroupMemberQuerySchema>;

// ============================================================================
// Response Schemas
// ============================================================================

export const GroupDiscountTierPublicSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nameId: z.string(),
  discountPercent: z.number(),
  requiresApproval: z.boolean(),
});
export type GroupDiscountTierPublic = z.infer<typeof GroupDiscountTierPublicSchema>;

export const GroupDiscountTierSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nameId: z.string(),
  groupType: GroupTypeSchema.nullable(),
  minSize: z.number().int(),
  maxSize: z.number().int().nullable(),
  discountPercent: z.number(),
  maxDiscountPerPersonIdr: z.number().int().nullable(),
  minEffectivePricePercent: z.number().nullable(),
  requiresApproval: z.boolean(),
  depositPercentRequired: z.number().nullable(),
  balanceDueDaysBefore: z.number().int().nullable(),
  minBookingDaysBefore: z.number().int().nullable(),
  applicableTicketTypes: z.array(z.string().uuid()),
  applicableDaysOfWeek: z.array(z.number().int().min(0).max(6)),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type GroupDiscountTier = z.infer<typeof GroupDiscountTierSchema>;

export const GroupMemberSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  customerId: z.string().nullable(),
  memberName: z.string(),
  memberEmail: z.string().nullable(),
  memberPhone: z.string().nullable(),
  status: GroupMemberStatusSchema,
  ticketId: z.string().nullable(),
  wristbandId: z.string().nullable(),
  wristbandDistribution: WristbandDistributionStatusSchema.nullable(),
  wristbandDistributedAt: z.string().nullable(),
  // Admin detail responses surface the check-in timestamp (from member metadata).
  checkInTime: z.string().nullable().optional(),
  spendingLimitIdr: z.number().int().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
});
export type GroupMember = z.infer<typeof GroupMemberSchema>;

export const TicketLineResultSchema = z.object({
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  quantity: z.number().int(),
  unitPriceIdr: z.number().int(),
  subtotalIdr: z.number().int(),
  discountIdr: z.number().int(),
  taxIdr: z.number().int(),
  totalIdr: z.number().int(),
});
export type TicketLineResult = z.infer<typeof TicketLineResultSchema>;

export const AddonLineResultSchema = z.object({
  attractionTicketTypeId: z.string().uuid(),
  attractionName: z.string(),
  quantity: z.number().int(),
  unitPriceIdr: z.number().int(),
  subtotalIdr: z.number().int(),
  taxIdr: z.number().int(),
  totalIdr: z.number().int(),
  bundleCode: z.string().optional(),
});
export type AddonLineResult = z.infer<typeof AddonLineResultSchema>;

export const CalculateGroupPriceResponseSchema = z.object({
  basePricePerPersonIdr: z.number().int(),
  dynamicMultiplier: z.number(),
  baseAfterDynamicIdr: z.number().int(),
  groupDiscountPercent: z.number(),
  effectivePricePerPersonIdr: z.number().int(),
  subtotalIdr: z.number().int(),
  addonSubtotalIdr: z.number().int(),
  couponDiscountIdr: z.number().int(),
  discountIdr: z.number().int(),
  taxIdr: z.number().int(),
  totalIdr: z.number().int(),
  paymentMode: GroupPaymentModeSchema,
  depositPercent: z.number(),
  depositAmountIdr: z.number().int(),
  balanceAmountIdr: z.number().int(),
  balanceDueDate: z.string().nullable(),
  tier: GroupDiscountTierPublicSchema.nullable(),
  requiresApproval: z.boolean(),
  ticketLineResults: z.array(TicketLineResultSchema),
  addonLineResults: z.array(AddonLineResultSchema),
});
export type CalculateGroupPriceResponse = z.infer<typeof CalculateGroupPriceResponseSchema>;

/**
 * Priced add-on line as serialized from a GroupOrderItem (see
 * `serializeGroupOrder` in groups.controller.ts). Surfaced to the web detail
 * view as the flattened `addonLines`.
 */
export const GroupOrderAddonItemSchema = z.object({
  id: z.string(),
  ticketTypeId: z.string(),
  ticketTypeName: z.string(),
  ticketTypeCode: z.string(),
  attractionName: z.string(),
  quantity: z.number().int(),
  unitPriceIdr: z.number().int(),
  subtotalIdr: z.number().int(),
  discountIdr: z.number().int(),
  totalIdr: z.number().int(),
});
export type GroupOrderAddonItem = z.infer<typeof GroupOrderAddonItemSchema>;

export const GroupBookingSchema = z.object({
  id: z.string().uuid(),
  bookingNumber: z.string(),
  groupOrderId: z.string().uuid().nullable(),
  status: GroupBookingStatusSchema,
  groupType: GroupTypeSchema,
  groupName: z.string(),
  coordinatorId: z.string().nullable(),
  coordinatorName: z.string(),
  coordinatorEmail: z.string(),
  coordinatorPhone: z.string().nullable(),
  expectedMembers: z.number().int(),
  confirmedMembers: z.number().int(),
  minMembers: z.number().int().nullable(),
  maxMembers: z.number().int().nullable(),
  visitDate: z.string(),
  arrivalTime: z.string().nullable(),
  specialRequirements: z.string().nullable(),
  schoolName: z.string().nullable(),
  emergencyContactName: z.string().nullable(),
  emergencyContactPhone: z.string().nullable().optional(),
  companyName: z.string().nullable(),
  purchaseOrderNumber: z.string().nullable(),
  basePricePerPersonIdr: z.number().int(),
  dynamicMultiplier: z.number(),
  groupDiscountPercent: z.number(),
  effectivePricePerPersonIdr: z.number().int(),
  subtotalIdr: z.number().int(),
  discountIdr: z.number().int(),
  taxIdr: z.number().int(),
  totalIdr: z.number().int(),
  paymentMode: GroupPaymentModeSchema,
  depositPercent: z.number(),
  depositAmountIdr: z.number().int(),
  depositPaidIdr: z.number().int(),
  depositPaidAt: z.string().nullable(),
  balanceAmountIdr: z.number().int(),
  balancePaidIdr: z.number().int(),
  balancePaidAt: z.string().nullable(),
  balanceDueDate: z.string().nullable(),
  couponCode: z.string().nullable(),
  couponDiscountIdr: z.number().int(),
  requiresApproval: z.boolean(),
  groupQrToken: z.string().nullable(),
  draftExpiresAt: z.string().nullable(),
  notes: z.string().nullable(),
  capacityWarning: z
    .object({
      hasWarning: z.boolean(),
      groupSize: z.number().int(),
      inventoryAvailable: z.number().int(),
      message: z.string().optional(),
    })
    .nullable(),
  discountTier: GroupDiscountTierPublicSchema.nullable(),
  // Flattened add-on lines (web detail view sets this from groupOrder.addonItems).
  addonLines: z.array(GroupOrderAddonItemSchema).optional(),
  // Sum of add-on subtotals, computed by admin-groups.service for the detail view.
  addonSubtotalIdr: z.number().int().optional(),
  // Raw add-on snapshot stored on the booking (present in list responses).
  addonLinesJson: z
    .array(
      z
        .object({ subtotalIdr: z.number().optional(), totalIdr: z.number().optional() })
        .passthrough()
    )
    .optional(),
  members: z.array(GroupMemberSchema).optional(),
  // Nested group-order container (present in detail responses; serializeGroupOrder).
  groupOrder: z
    .object({
      id: z.string().uuid(),
      members: z.array(GroupMemberSchema).optional(),
      addonItems: z.array(GroupOrderAddonItemSchema).optional(),
      _count: z.object({ members: z.number().int() }).optional(),
    })
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type GroupBooking = z.infer<typeof GroupBookingSchema>;

/**
 * Structured JSON metadata attached to a group booking by the backend.
 * Open-ended (passthrough) since the backend may attach additional keys.
 * Mirrors `GroupBookingMetadata` in apps/admin/src/types/groups.ts.
 */
export const GroupBookingMetadataSchema = z
  .object({
    lastAdminUpdate: z
      .object({
        paymentProofUrl: z.string().optional(),
        updatedAt: z.string().optional(),
        updatedBy: z.string().optional(),
      })
      .optional(),
    fraudFlags: z.array(z.string()).optional(),
    similarBookingNumber: z.string().optional(),
    sizeInflationDetected: z.boolean().optional(),
    sizeInflationDebt: z.number().optional(),
    actualAttendance: z.number().optional(),
    resellerAbuseFlagged: z.boolean().optional(),
  })
  .passthrough();
export type GroupBookingMetadata = z.infer<typeof GroupBookingMetadataSchema>;

export const GroupBookingTicketLineSchema = z.object({
  id: z.string().optional(),
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string().optional(),
  quantity: z.number().int(),
  unitPriceIdr: z.number().int(),
  subtotalIdr: z.number().int(),
  discountIdr: z.number().int(),
  taxIdr: z.number().int(),
  totalIdr: z.number().int(),
});
export type GroupBookingTicketLine = z.infer<typeof GroupBookingTicketLineSchema>;

export const GroupBookingAuditEntrySchema = z.object({
  at: z.string(),
  by: z.string().nullable(),
  action: z.string(),
  detail: z.string().optional(),
  previousStatus: z.string().optional(),
  newStatus: z.string().optional(),
});
export type GroupBookingAuditEntry = z.infer<typeof GroupBookingAuditEntrySchema>;

export const AdminGroupBookingSchema = GroupBookingSchema.extend({
  ticketLines: z.array(GroupBookingTicketLineSchema).optional(),
  auditLog: z.array(GroupBookingAuditEntrySchema).optional(),
  resellerId: z.string().nullable(),
  resellerBookingRef: z.string().nullable(),
  resellerCommissionIdr: z.number().int().nullable(),
  adminNotes: z.string().nullable(),
  metadata: GroupBookingMetadataSchema.nullable(),
  ipAddress: z.string().nullable(),
  sourceChannel: z.string().nullable(),
});
export type AdminGroupBooking = z.infer<typeof AdminGroupBookingSchema>;

/** Group list pagination envelope (`{ data, meta }`) — matches the controller responses. */
export const GroupListMetaSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
});
export type GroupListMeta = z.infer<typeof GroupListMetaSchema>;

export const PaginatedGroupBookingsSchema = z.object({
  data: z.array(GroupBookingSchema),
  meta: GroupListMetaSchema,
});
export type PaginatedGroupBookings = z.infer<typeof PaginatedGroupBookingsSchema>;

export const AdminPaginatedGroupBookingsSchema = z.object({
  success: z.boolean().optional(),
  data: z.array(AdminGroupBookingSchema),
  meta: GroupListMetaSchema,
});
export type AdminPaginatedGroupBookings = z.infer<typeof AdminPaginatedGroupBookingsSchema>;

export const PaginatedGroupMembersSchema = z.object({
  data: z.array(GroupMemberSchema),
  meta: GroupListMetaSchema,
});
export type PaginatedGroupMembers = z.infer<typeof PaginatedGroupMembersSchema>;

// ============================================================================
// Admin operational schemas (dashboard stats, legacy group-order, discount tiers)
// ============================================================================

export const AdminGroupStatsSchema = z.object({
  totalGroups: z.number().int(),
  pendingApproval: z.number().int(),
  todayCheckIns: z.number().int(),
  totalRevenue: z.number(),
  avgGroupSize: z.number(),
  paidToday: z.number(),
  confirmedPipelineValue: z.number(),
  checkedInToday: z.number().int(),
  byStatus: z.record(GroupBookingStatusSchema, z.number()),
  byGroupType: z.record(GroupTypeSchema, z.number()),
  revenueByGroupType: z.record(GroupTypeSchema, z.number()),
  conversionFunnel: z.object({
    draft: z.number().int(),
    confirmed: z.number().int(),
    depositPaid: z.number().int(),
    paid: z.number().int(),
  }),
  groupSizeDistribution: z.array(z.object({ label: z.string(), count: z.number().int() })),
});
export type AdminGroupStats = z.infer<typeof AdminGroupStatsSchema>;

export const AdminGroupBookingsQuerySchema = z.object({
  status: GroupBookingStatusSchema.optional(),
  groupType: GroupTypeSchema.optional(),
  visitDateFrom: z.string().optional(),
  visitDateTo: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  sortBy: z.enum(['createdAt', 'visitDate', 'totalIdr', 'status', 'bookingNumber', 'expectedMembers']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  sourceChannel: z.enum(['web', 'reseller', 'kiosk']).optional(),
});
export type AdminGroupBookingsQuery = z.infer<typeof AdminGroupBookingsQuerySchema>;

// Discount tier requests (admin CRUD). The response shape is GroupDiscountTierSchema.
export const DayOfWeekSchema = z.number().int().min(0).max(6);
export type DayOfWeek = z.infer<typeof DayOfWeekSchema>;

export const CreateDiscountTierRequestSchema = z.object({
  name: z.string().min(1),
  nameId: z.string().min(1),
  groupType: GroupTypeSchema,
  minSize: z.number().int(),
  maxSize: z.number().int(),
  discountPercent: z.number(),
  maxDiscountPerPersonIdr: z.number().int().nullable().optional(),
  minEffectivePricePercent: z.number().optional(),
  depositPercentRequired: z.number(),
  balanceDueDaysBefore: z.number().int(),
  minBookingDaysBefore: z.number().int(),
  requiresApproval: z.boolean(),
  applicableTicketTypes: z.array(z.string().uuid()).optional(),
  applicableDaysOfWeek: z.array(DayOfWeekSchema),
  validFrom: z.string().nullable().optional(),
  validUntil: z.string().nullable().optional(),
  isActive: z.boolean(),
  sortOrder: z.number().int().optional(),
});
export type CreateDiscountTierRequest = z.infer<typeof CreateDiscountTierRequestSchema>;

export const UpdateDiscountTierRequestSchema = CreateDiscountTierRequestSchema.partial();
export type UpdateDiscountTierRequest = z.infer<typeof UpdateDiscountTierRequestSchema>;

// Legacy group-order (3/5-state operational container, /v1/admin/groups/:id)
export const GroupOrderSchema = z.object({
  id: z.string().uuid(),
  groupNumber: z.string(),
  coordinatorId: z.string(),
  coordinatorName: z.string().nullable(),
  coordinatorEmail: z.string().nullable(),
  coordinatorPhone: z.string().nullable(),
  groupName: z.string().nullable(),
  visitDate: z.string(),
  status: GroupBookingStatusSchema,
  totalMembers: z.number().int(),
  maxMembers: z.number().int(),
  minMembers: z.number().int(),
  defaultSpendingLimitIdr: z.number().int().nullable(),
  checkedInCount: z.number().int(),
  checkInStartedAt: z.string().nullable(),
  checkInCompletedAt: z.string().nullable(),
  orderId: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  members: z.array(GroupMemberSchema).optional(),
  _count: z.object({ members: z.number().int() }).optional(),
});
export type GroupOrder = z.infer<typeof GroupOrderSchema>;

export const GroupOrderStatsSchema = z.object({
  group: GroupOrderSchema,
  memberStats: z.object({
    total: z.number().int(),
    pending: z.number().int(),
    confirmed: z.number().int(),
    checkedIn: z.number().int(),
    noShow: z.number().int(),
    cancelled: z.number().int(),
  }),
  wristbandStats: z.object({
    held: z.number().int(),
    distributed: z.number().int(),
    latePickup: z.number().int(),
  }),
});
export type GroupOrderStats = z.infer<typeof GroupOrderStatsSchema>;

export const CreateGroupOrderRequestSchema = z.object({
  // Same column widths as CreateGroupBookingSchema above, same reason (#870):
  // group_orders.coordinator_name VarChar(100), coordinator_email VarChar(200),
  // coordinator_phone VarChar(30).
  coordinatorName: z.string().min(1).max(100),
  coordinatorEmail: z.string().email().max(200),
  coordinatorPhone: z.string().max(30).optional(),
  groupName: z.string().optional(),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  maxMembers: z.number().int().optional(),
  minMembers: z.number().int().optional(),
  defaultSpendingLimitIdr: z.number().int().optional(),
  notes: z.string().optional(),
});
export type CreateGroupOrderRequest = z.infer<typeof CreateGroupOrderRequestSchema>;

export const UpdateGroupOrderRequestSchema = z.object({
  coordinatorName: z.string().max(100).optional(),
  coordinatorEmail: z.string().email().max(200).optional(),
  coordinatorPhone: z.string().max(30).optional(),
  groupName: z.string().optional(),
  maxMembers: z.number().int().optional(),
  defaultSpendingLimitIdr: z.number().int().optional(),
  notes: z.string().optional(),
});
export type UpdateGroupOrderRequest = z.infer<typeof UpdateGroupOrderRequestSchema>;

export const DistributeWristbandRequestSchema = z.object({
  wristbandId: z.string().min(1),
});
export type DistributeWristbandRequest = z.infer<typeof DistributeWristbandRequestSchema>;

// ============================================================================
// Check-in Schemas (GRP-013)
// ============================================================================

export const StartCheckInSchema = z.object({
  gateId: z.string().min(1),
});
export type StartCheckInRequest = z.infer<typeof StartCheckInSchema>;

export const CheckInMemberSchema = z.object({
  memberId: z.string().min(1),
  wristbandCode: z.string().length(8),
  gateId: z.string().optional(),
});
export type CheckInMemberRequest = z.infer<typeof CheckInMemberSchema>;

export const BatchCheckInMemberSchema = z.object({
  memberId: z.string().min(1),
  wristbandCode: z.string().length(8),
});

export const BatchCheckInSchema = z.object({
  gateId: z.string().optional(),
  members: z.array(BatchCheckInMemberSchema).min(1),
});
export type BatchCheckInRequest = z.infer<typeof BatchCheckInSchema>;

export const ScanGroupQrSchema = z.object({
  qrToken: z.string().length(52),
});
export type ScanGroupQrRequest = z.infer<typeof ScanGroupQrSchema>;

// -- Check-in Response Schemas --

export const StartCheckInResponseSchema = z.object({
  bookingNumber: z.string(),
  status: z.literal('CHECKED_IN'),
  gateId: z.string(),
  startedAt: z.string(),
});
export type StartCheckInResponse = z.infer<typeof StartCheckInResponseSchema>;

export const CheckInMemberResponseSchema = z.object({
  memberId: z.string(),
  memberName: z.string(),
  status: z.literal('CHECKED_IN'),
  wristbandCode: z.string().nullable(),
  checkedInAt: z.string(),
  checkedInMembersSoFar: z.number().int(),
  totalMembers: z.number().int(),
});
export type CheckInMemberResponse = z.infer<typeof CheckInMemberResponseSchema>;

export const BatchCheckInProgressSchema = z.object({
  total: z.number().int(),
  checkedIn: z.number().int(),
  remaining: z.number().int(),
  percentComplete: z.number().int(),
});

export const BatchCheckInResponseSchema = z.object({
  processed: z.number().int(),
  succeeded: z.number().int(),
  failed: z.number().int(),
  errors: z.array(z.object({
    memberId: z.string(),
    error: z.string(),
  })),
  progress: BatchCheckInProgressSchema,
});
export type BatchCheckInResponse = z.infer<typeof BatchCheckInResponseSchema>;

export const CheckInStatusResponseSchema = z.object({
  bookingNumber: z.string(),
  status: z.string(),
  total: z.number().int(),
  checkedIn: z.number().int(),
  noShow: z.number().int(),
  remaining: z.number().int(),
  percentComplete: z.number().int(),
  source: z.enum(['cache', 'db']),
  checkInGateId: z.string().optional(),
  checkInStartedAt: z.string().optional(),
});
export type CheckInStatusResponse = z.infer<typeof CheckInStatusResponseSchema>;

export const CompleteCheckInResponseSchema = z.object({
  bookingNumber: z.string(),
  status: z.literal('COMPLETED'),
  checkedIn: z.number().int(),
  noShow: z.number().int(),
  noShowNames: z.array(z.string()),
  completedAt: z.string(),
});
export type CompleteCheckInResponse = z.infer<typeof CompleteCheckInResponseSchema>;

export const ScanGroupQrResponseSchema = z.object({
  bookingNumber: z.string(),
  groupBookingId: z.string(),
  groupName: z.string(),
  coordinatorName: z.string(),
  totalMembers: z.number().int(),
  checkedIn: z.number().int(),
  noShow: z.number().int(),
  remaining: z.number().int(),
  source: z.enum(['cache', 'db']),
});
export type ScanGroupQrResponse = z.infer<typeof ScanGroupQrResponseSchema>;

// ============================================================================
// Admin Group Operation Schemas (GRP-008)
// ============================================================================

export const AdminGroupStatsQuerySchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});
export type AdminGroupStatsQuery = z.infer<typeof AdminGroupStatsQuerySchema>;

export const AdminApproveGroupSchema = z.object({
  notes: z.string().optional(),
  overrideDiscountPercent: z.number().min(0).max(50).optional(),
  resellerCommissionIdr: z.number().min(0).optional(),
  resellerId: z.string().optional(),
});
export type AdminApproveGroupRequest = z.infer<typeof AdminApproveGroupSchema>;

export const AdminRejectGroupSchema = z.object({
  reason: z.string().min(1).max(1000),
});
export type AdminRejectGroupRequest = z.infer<typeof AdminRejectGroupSchema>;

export const AdminUpdateGroupSchema = z.object({
  notes: z.string().optional(),
  overrideDiscountPercent: z.number().min(0).max(50).optional(),
  specialRequirements: z.string().optional(),
  resellerId: z.string().optional(),
  resellerCommissionIdr: z.number().min(0).optional(),
});
export type AdminUpdateGroupRequest = z.infer<typeof AdminUpdateGroupSchema>;

export const AdminCancelGroupSchema = z.object({
  reason: z.string().optional(),
});
export type AdminCancelGroupRequest = z.infer<typeof AdminCancelGroupSchema>;

export const AdminMarkPaidSchema = z.object({
  paymentReference: z.string().max(100).optional(),
  notes: z.string().max(500).optional(),
});
export type AdminMarkPaidRequest = z.infer<typeof AdminMarkPaidSchema>;

export const AdminBulkCheckInSchema = z.object({
  memberIds: z.array(z.string().uuid()).min(1),
});
export type AdminBulkCheckInRequest = z.infer<typeof AdminBulkCheckInSchema>;
